import { useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Car, Truck, Pencil, Trash2, X, Check } from "lucide-react";
import { Panel, Inp, Sel, Btn, Th, Td, RT } from "./ui";

// Generic structured sub-record module: PERSONS / VEHICLES / UNITS.
// One editor component drives all three (add + edit + remove, all audited).

const CONFIGS = {
  persons: {
    icon: UserPlus, addLabel: "ADD PERSON", empty: "NO INVOLVED PERSONS",
    cols: [["NAME", (p) => `${(p.last_name || "").toUpperCase()}, ${(p.first_name || "").toUpperCase()}`.replace(/^, |, $/g, "") || p.alias || "UNKNOWN"],
      ["ROLE", (p) => p.role], ["SEX", (p) => p.sex], ["DOB", (p) => p.dob],
      ["AGE", (p) => p.age || (p.dob ? Math.max(0, Math.floor((Date.now() - new Date(p.dob)) / 3.15576e10)) || "" : "")],
      ["STATUS", (p) => p.transport_status || p.status]],
    key: "pid",
    fields: [["first_name", "First Name"], ["middle_name", "Middle Name"], ["last_name", "Last Name"], ["suffix", "Suffix"],
      ["alias", "Alias"], ["dob", "DOB (MM/DD/YYYY)"], ["age", "Approx Age"], ["sex", "Sex", ["M", "F", "X", "U"]],
      ["race", "Race (if lawfully collected)"], ["height", "Height"], ["weight", "Weight"], ["hair", "Hair"], ["eyes", "Eyes"],
      ["address", "Address"], ["phone", "Telephone"], ["email", "Email"], ["id_type", "ID Type"], ["id_number", "ID Number"],
      ["emergency_contact", "Emergency Contact"], ["housing_status", "Housing Status"],
      ["transport_status", "Medical Transport Status", ["", "TRANSPORTED", "TREATED/RELEASED", "REFUSED", "N/A"]],
      ["role", "Role", "PERSON_ROLES"], ["notes", "Notes"]],
  },
  vehicles: {
    icon: Car, addLabel: "ADD VEHICLE", empty: "NO VEHICLES INVOLVED",
    cols: [["YEAR", (v) => v.year], ["MAKE", (v) => v.make], ["MODEL", (v) => v.model], ["COLOR", (v) => v.color],
      ["TYPE", (v) => v.vtype], ["PLATE", (v) => v.plate], ["STATE", (v) => v.state], ["VIN", (v) => (v.vin || "").slice(0, 10)], ["ROLE", (v) => v.role]],
    key: "vid",
    fields: [["year", "Year"], ["make", "Make"], ["model", "Model"], ["color", "Color"], ["vtype", "Type"],
      ["plate", "License Plate"], ["state", "State"], ["vin", "VIN"],
      ["role", "Role", ["Suspect Vehicle", "Victim Vehicle", "Involved Vehicle", "Witness Vehicle", "Towed Vehicle", "Recovered Vehicle", "Other"]],
      ["notes", "Notes"]],
  },
  units: {
    icon: Truck, addLabel: "ADD UNIT", empty: "NO RESPONDING UNITS",
    cols: [["UNIT", (u) => u.unit], ["OFFICER / CREW", (u) => u.officer], ["ROLE", (u) => u.role],
      ["TIME ON SCENE", (u) => u.time_on_scene], ["DISPOSITION", (u) => u.disposition]],
    key: "uid",
    fields: [["unit", "Unit"], ["officer", "Officer / Crew"],
      ["role", "Role", ["Primary", "Assisting", "Supervisor", "Backup", "EMS", "Fire", "Investigator", "Traffic", "Specialty Unit", "Outside Agency"]],
      ["time_on_scene", "Time On Scene (24h)"], ["disposition", "Disposition", ["", "CLEARED", "TRANSPORTED", "REPORT TAKEN", "ARREST", "OTHER"]]],
  },
};

function Editor({ sub, cfg, rep, initial, onDone, personRoles }) {
  const [f, setF] = useState(initial || {});
  const save = async () => {
    try {
      if (initial?.[cfg.key]) await client.patch(`/rms-reports/${rep.id}/records/${sub}/${initial[cfg.key]}`, f);
      else await client.post(`/rms-reports/${rep.id}/records/${sub}`, f);
      toast.success("Record saved");
      onDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="border rounded-sm p-3 mt-2" style={{ borderColor: RT.head, background: "#eef4f9" }} data-testid={`${sub}-editor`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cfg.fields.map(([k, label, opts]) => {
          const options = opts === "PERSON_ROLES" ? personRoles : opts;
          return options
            ? <Sel key={k} label={label} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} options={options.filter(Boolean)} testid={`${sub}-f-${k}`} />
            : <Inp key={k} label={label} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testid={`${sub}-f-${k}`} />;
        })}
      </div>
      <div className="flex gap-2 mt-2">
        <Btn onClick={save} testid={`${sub}-editor-save`}><Check size={12} /> Save</Btn>
        <Btn onClick={() => onDone(false)} color="#64748b" testid={`${sub}-editor-cancel`}><X size={12} /> Cancel</Btn>
      </div>
    </div>
  );
}

export function SubModule({ sub, rep, canEdit, onChanged, personRoles }) {
  const cfg = CONFIGS[sub];
  const [editing, setEditing] = useState(null); // null | {} | record
  const rows = rep[sub] || [];
  const remove = async (r) => {
    if (!window.confirm("Remove this record from the incident? (audited)")) return;
    try { await client.delete(`/rms-reports/${rep.id}/records/${sub}/${r[cfg.key]}`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };
  const Icon = cfg.icon;
  return (
    <Panel title={`${sub === "units" ? "Responding Units" : "Involved " + sub} (${rows.length})`} testid={`panel-${sub}`}
      right={canEdit && <Btn small onClick={() => setEditing({})} testid={`${sub}-add-btn`}><Icon size={11} /> {cfg.addLabel}</Btn>}>
      {rows.length === 0 ? (
        <div className="font-mono text-[11px] py-3 text-center" style={{ color: RT.dim }}>{cfg.empty}</div>
      ) : (
        <table className="w-full">
          <thead><tr>{cfg.cols.map(([h]) => <Th key={h}>{h}</Th>)}{canEdit && <Th> </Th>}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[cfg.key]} data-testid={`${sub}-row-${r[cfg.key]}`}>
                {cfg.cols.map(([h, fn]) => <Td key={h}>{fn(r) || "—"}</Td>)}
                {canEdit && <Td>
                  <div className="flex gap-1.5">
                    <button data-testid={`${sub}-edit-${r[cfg.key]}`} onClick={() => setEditing(r)} className="text-[#1d5d90]"><Pencil size={13} /></button>
                    <button data-testid={`${sub}-del-${r[cfg.key]}`} onClick={() => remove(r)} className="text-[#b91c1c]"><Trash2 size={13} /></button>
                  </div>
                </Td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing !== null && (
        <Editor sub={sub} cfg={cfg} rep={rep} initial={editing[cfg.key] ? editing : null} personRoles={personRoles}
          onDone={(saved) => { setEditing(null); if (saved) onChanged(); }} />
      )}
    </Panel>
  );
}

export default function SectionEntities({ rep, canEdit, onChanged, meta, only }) {
  const subs = only ? [only] : ["persons", "vehicles", "units"];
  return (
    <div className="space-y-3">
      {subs.map((s) => <SubModule key={s} sub={s} rep={rep} canEdit={canEdit} onChanged={onChanged} personRoles={meta.person_roles} />)}
    </div>
  );
}
